import { convertWeiToEth, getErrorMessage, timeMeasurement } from '@pkg/utils';

import { useExchangeRatesStore } from '@app/store/zustand';

import { type CommonNftQueryOptions } from './types';
import { useCollectionAssetsQuery } from './useCollectionAssetsQuery';
import { useCollectionTokensQuery } from './useCollectionTokensQuery';
import { useNftDetailsQuery } from './useNftDetailsQuery';
import { guessBlockchain, nativeCurrencyFromChain, percentFloat } from './utilities';

export function useNftDataStream({ contractAddress, tokenId, disabled }: CommonNftQueryOptions) {
    const convertCurrency = useExchangeRatesStore((state) => state.convertCurrency);

    // TODO: Consider passing `refetchInterval`.
    // TODO: Not currently passing `keepPreviousData` because it will
    // initialize our UI with NFT data from a previously fetched asset.
    const commonOptions: CommonNftQueryOptions = { contractAddress, tokenId, disabled };

    const {
        data: dataAssets,
        error: errorAssets,
        isFetching: fetchingAssets,
    } = useCollectionAssetsQuery(commonOptions);
    const {
        data: dataTokens,
        error: errorTokens,
        isFetching: fetchingTokens,
    } = useCollectionTokensQuery(commonOptions);

    const { data: dataDetails, error: errorDetails, isFetching: fetchingDetails } = useNftDetailsQuery(commonOptions);

    const firstToken = dataTokens?.tokens[0];
    const name = firstToken?.token?.name;
    const blockchain = guessBlockchain(dataDetails?.blockchain);
    const nativeCurrency = nativeCurrencyFromChain(blockchain);

    const appraisedWeiValue = dataAssets?.appraisal?.wei ? parseInt(dataAssets?.appraisal?.wei) : 0;
    const appraisedEthValue = convertWeiToEth(appraisedWeiValue, 10);
    const appraisedUsdValue = convertCurrency('eth', 'usd', appraisedEthValue, true);

    // TODO: Some response values can be `null`, even though that is not
    // what is reported by their types. Hence the `?? undefined` conditions.
    const initialFloorData = {
        ethValue: dataDetails?.floor_price ?? undefined,
        eth24hChange: dataDetails?.one_day_change,
    };

    const initialLastSaleData = {
        ethValue: firstToken?.token?.lastSell?.value,
        timestamp: firstToken?.token?.lastSell?.timestamp,
    };

    // Should we fallback to `undefined` instead of `0`?
    const lastSaleUtcMs =
        initialLastSaleData.ethValue !== undefined && initialLastSaleData.timestamp !== undefined
            ? initialLastSaleData.timestamp * timeMeasurement.msPerSec
            : 0;

    // TODO: Temporary, as we are currently have many issues with the `call*` requests.
    const errors: string[] = [];
    if (errorAssets) errors.push(getErrorMessage(errorAssets));
    if (errorTokens) errors.push(getErrorMessage(errorTokens));
    if (errorDetails) errors.push(getErrorMessage(errorDetails));

    return {
        loading: Boolean(fetchingAssets || fetchingTokens || fetchingDetails),
        blockchain,
        nativeCurrency,
        tokenId,
        contractAddress,
        name,
        collectionName: dataAssets?.collection?.name,
        imageUrl: firstToken?.token?.image,
        appraisal: {
            weiValue: appraisedWeiValue,
            ethValue: appraisedEthValue,
            usdValue: appraisedUsdValue,
        },
        floorData: {
            ...initialFloorData,
            usdValue: convertCurrency('eth', 'usd', initialFloorData.ethValue, true),
            usd24hChange: convertCurrency('eth', 'usd', initialFloorData.eth24hChange, true),
            percentChange: percentFloat(initialFloorData.ethValue, initialFloorData.eth24hChange),
        },
        lastSaleData: {
            ...initialLastSaleData,
            utcMs: lastSaleUtcMs,
            usdValue: convertCurrency('eth', 'usd', initialLastSaleData.ethValue, true),
        },
        errors: errors.length ? errors : undefined,
    };
}
